import { Alert, Button, Icon, Link, Text } from "@alpacahq/ui/old";

export type PlaidStatus =
  | "idle"
  | "plaid_open"
  | "processing"
  | "completed"
  | "error";

export interface PlaidStatusPanelProps {
  status: PlaidStatus;
  /** Plaid Link finished initializing and `open()` is safe to call. */
  isPlaidReady: boolean;
  /**
   * True when the URL carries `micro_deposit_verification_required=true`; flips
   * the CTA copy from "link" to "verify".
   */
  isVerifyingMicroDeposit: boolean;
  /**
   * Show the "bank already linked" terminal state instead of the open CTA. Only
   * the fresh-link page sets this; the OAuth-resume page never does.
   */
  showAlreadyLinked: boolean;
  /**
   * True while the browser is in full-screen mode, which Chrome downgrades
   * Plaid's OAuth pop-up into a tab for, silently breaking the handoff back
   * into Link (PRIME-584). Blocks the open CTA. Only the fresh-link page sets
   * this; the OAuth-resume page passes false so a user returning from their
   * bank is never stranded mid-resume.
   */
  isFullscreen: boolean;
  onOpenPlaid: () => void;
  onBack: () => void;
}

/**
 * Pure status box for the ACH link flow (idle / plaid_open / processing /
 * completed / error / already-linked). No hooks — all state arrives via props
 * so both the fresh-link and OAuth-resume pages render an identical panel.
 */
export const PlaidStatusPanel = ({
  status,
  isPlaidReady,
  isVerifyingMicroDeposit,
  showAlreadyLinked,
  isFullscreen,
  onOpenPlaid,
  onBack,
}: PlaidStatusPanelProps) => {
  return (
    <div className="w-full max-w-lg rounded border border-gray-200 p-4">
      {status === "completed" ? (
        <div className="py-4">
          <Text className="font-bold text-xl">
            Account linked successfully! You will be redirected momentarily.
          </Text>
        </div>
      ) : status === "plaid_open" || status === "processing" ? (
        <div className="py-4">
          <Alert
            className="mb-4"
            message={
              <span className="flex gap-4">
                <Icon name="InformationCircle" />
                <span>
                  If you're experiencing issues connecting to your Plaid
                  account, please ensure that <b>pop-ups are enabled</b>.
                </span>
              </span>
            }
            variant="warning"
          />

          <Text className="font-bold text-xl">
            Connection pending, please wait. This should only take a few
            moments.
          </Text>
        </div>
      ) : status === "error" ? (
        <div className="py-4">
          <Alert
            className="mb-4"
            message={
              <span className="flex gap-4">
                <Icon name="InformationCircle" />
                <span>
                  Something went wrong while linking your bank account. Please
                  try again. If the issue persists, contact{" "}
                  <Link href="mailto:support@alpaca.markets">
                    support@alpaca.markets
                  </Link>
                </span>
              </span>
            }
            variant="danger"
          />

          <Button onClick={onBack} variant="secondary">
            <Icon name="ArrowLeftCircle" className="mr-2" />
            Go Back
          </Button>
        </div>
      ) : showAlreadyLinked && !isVerifyingMicroDeposit ? (
        <>
          <Text className="pb-4 font-bold text-xl">
            Bank account already linked
          </Text>
          <Text className="pb-4">
            Only one bank account can be linked at a time. To link a new bank
            please unlink the existing bank account.
          </Text>
          <Button variant="secondary" onClick={onBack}>
            <Icon name="ArrowLeftCircle" className="mr-2" />
            Back
          </Button>
        </>
      ) : (
        <>
          <Text className="font-bold text-xl">
            {isVerifyingMicroDeposit
              ? "Plaid needs verification that you received a micro-deposit to your bank account"
              : "Securely link your bank account to Alpaca via Plaid"}
          </Text>
          {isFullscreen ? (
            <Alert
              className="mt-4"
              message={
                <span className="flex gap-4">
                  <Icon name="InformationCircle" />
                  <span>
                    Plaid can't open its secure bank sign-in window while your
                    browser is in <b>full-screen mode</b>. Exit full-screen mode
                    to continue.
                  </span>
                </span>
              }
              variant="warning"
            />
          ) : null}
          <Button
            onClick={onOpenPlaid}
            disabled={!isPlaidReady || isFullscreen}
            className="mt-4 w-full"
          >
            {!isPlaidReady
              ? "Plaid is Loading..."
              : isVerifyingMicroDeposit
                ? "Verify Micro-Deposit"
                : "Open Plaid"}
          </Button>
        </>
      )}
    </div>
  );
};
